import React from 'react';
import { Facebook, Phone, Mail, MapPin, Clock, ChevronUp, Heart, Shield, Wrench, Package } from 'lucide-react';
import { SITE_CONFIG } from '@/lib/storage';

export const Footer: React.FC = () => {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <footer className="border-t border-dark-border bg-dark-card/50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
        {/* Brand at top center - 20% larger (text-3xl -> text-4xl) */}
        <div className="text-center mb-12 lg:mb-16">
          <a href="/" className="inline-flex flex-col items-center gap-3" aria-label="JB Motorcycles Home">
            <div className="w-16 h-16 rounded-xl bg-gold/10 flex items-center justify-center">
              <Heart className="h-9 w-9 text-gold" />
            </div>
            <span className="font-black text-4xl tracking-tight text-white">JB Motorcycles</span>
          </a>
        </div>

        {/* Spare Parts & Workshop highlight */}
        <div className="text-center mb-12 lg:mb-16">
          <p className="text-neutral-400 text-lg sm:text-xl max-w-3xl mx-auto leading-relaxed">
            <span className="font-semibold text-gold">Spare Parts Sales</span> — Wide variety of new and secondhand spare parts available. We stock service parts for most major and Chinese brands.
            <br className="hidden sm:block" />
            <span className="font-semibold text-gold">Workshop & Service Center</span> — With over 20 years of motorcycle mechanics experience, we repair all bikes with care.
          </p>
        </div>

        {/* 3-column grid: Contact, Location & Hours, Why Choose Us */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12 mb-12">
          {/* Contact Us */}
          <div>
            <h3 className="font-bold text-sm uppercase tracking-wider text-white mb-4">Contact Us</h3>
            <div className="space-y-3 text-sm">
              <a
                href={`tel:${SITE_CONFIG.phone.mobile.replace(/\s/g, '')}`}
                className="flex items-center gap-3 text-neutral-400 hover:text-gold transition-colors"
              >
                <div className="h-8 w-8 rounded-lg bg-gold/10 flex items-center justify-center flex-shrink-0">
                  <Phone className="h-4 w-4 text-gold" />
                </div>
                <span>{SITE_CONFIG.phone.mobile}</span>
              </a>
              <a
                href={`tel:${SITE_CONFIG.phone.landline.replace(/\s/g, '')}`}
                className="flex items-center gap-3 text-neutral-400 hover:text-gold transition-colors"
              >
                <div className="h-8 w-8 rounded-lg bg-gold/10 flex items-center justify-center flex-shrink-0">
                  <Phone className="h-4 w-4 text-gold" />
                </div>
                <span>{SITE_CONFIG.phone.landline} (Landline)</span>
              </a>
              <a
                href={`mailto:${SITE_CONFIG.email.sales}`}
                className="flex items-center gap-3 text-neutral-400 hover:text-gold transition-colors"
              >
                <div className="h-8 w-8 rounded-lg bg-gold/10 flex items-center justify-center flex-shrink-0">
                  <Mail className="h-4 w-4 text-gold" />
                </div>
                <span>{SITE_CONFIG.email.sales}</span>
              </a>
              <a
                href="https://www.facebook.com/jbmotorcycle/"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 text-neutral-400 hover:text-gold transition-colors"
              >
                <div className="h-8 w-8 rounded-lg bg-gold/10 flex items-center justify-center flex-shrink-0">
                  <Facebook className="h-4 w-4 text-gold" />
                </div>
                <span>Facebook</span>
              </a>
            </div>
          </div>

          {/* Location & Hours */}
          <div>
            <h3 className="font-bold text-sm uppercase tracking-wider text-white mb-4">Location & Hours</h3>
            <div className="space-y-3 text-sm">
              <div className="flex items-start gap-3">
                <div className="h-8 w-8 rounded-lg bg-gold/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <MapPin className="h-4 w-4 text-gold" />
                </div>
                <span className="leading-relaxed text-neutral-400">{SITE_CONFIG.address}</span>
              </div>
              <div className="flex items-start gap-3">
                <div className="h-8 w-8 rounded-lg bg-gold/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Clock className="h-4 w-4 text-gold" />
                </div>
                <div className="leading-relaxed text-neutral-400">
                  <p>{SITE_CONFIG.hours.weekday}</p>
                  <p>{SITE_CONFIG.hours.saturday}</p>
                  <p className="text-neutral-600">Closed Sundays & Public Holidays</p>
                </div>
              </div>
            </div>
          </div>

          {/* Why Choose Us */}
          <div>
            <h3 className="font-bold text-sm uppercase tracking-wider text-white mb-4">Why Choose Us</h3>
            <div className="space-y-3">
              {[
                { icon: Shield, text: 'Every bike thoroughly inspected & certified' },
                { icon: Wrench, text: 'Full workshop & service center on-site' },
                { icon: Package, text: 'Genuine spare parts & accessories in stock' },
                { icon: Heart, text: 'Finance options available for all models' },
              ].map(({ icon: Icon, text }, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className="h-8 w-8 rounded-lg bg-gold/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Icon className="h-4 w-4 text-gold" />
                  </div>
                  <span className="text-sm text-neutral-400 leading-relaxed">{text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom: Copyright + Scroll to top */}
        <div className="pt-8 border-t border-dark-border">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-neutral-500 text-sm">
              &copy; {new Date().getFullYear()} JB Motorcycles PTY LTD. All rights reserved.
            </p>
            <button
              onClick={scrollToTop}
              className="fixed bottom-6 right-6 lg:static h-10 w-10 rounded-xl bg-gold/10 border border-gold/30 flex items-center justify-center hover:bg-gold/20 transition-colors"
              aria-label="Scroll to top">
              <ChevronUp className="h-5 w-5 text-gold" />
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
};